import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, Save } from "lucide-react";

type Draft = { title: string; excerpt: string; content: string };
type Drafts = { retail: Draft; ifa: Draft };

const VIRTUAL_AUTHOR = "a0000000-0000-0000-0000-000000000001";

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 80);
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  rebalanceDate: string;
}

export function RRebalanceNotesDialog({ open, onOpenChange, rebalanceDate }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [feedback, setFeedback] = useState("");

  const generate = async (previous?: Drafts, fb?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("r-rebalance-notes", {
        body: {
          rebalance_date: rebalanceDate,
          ...(previous && fb ? { previous, feedback: fb } : {}),
        },
      });
      if (error) throw error;
      setDrafts(data as Drafts);
      if (fb) {
        setFeedback("");
        toast.success("Drafts revised");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Draft generation failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !drafts && !loading) {
      generate();
    }
    if (!open) {
      setDrafts(null);
      setFeedback("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (key: "retail" | "ifa", field: keyof Draft, value: string) => {
    if (!drafts) return;
    setDrafts({ ...drafts, [key]: { ...drafts[key], [field]: value } });
  };

  const saveDrafts = async () => {
    if (!drafts) return;
    setSaving(true);
    try {
      const stamp = Date.now();
      const rows = [
        {
          author_id: VIRTUAL_AUTHOR,
          title: drafts.retail.title,
          slug: `${slugify(drafts.retail.title) || "r-rebalance-client-note"}-${stamp}`,
          excerpt: drafts.retail.excerpt,
          content: drafts.retail.content,
          blog_types: ["public"],
          status: "draft",
          category: "Portfolio Update",
        },
        {
          author_id: VIRTUAL_AUTHOR,
          title: drafts.ifa.title,
          slug: `${slugify(drafts.ifa.title) || "r-rebalance-adviser-note"}-${stamp}`,
          excerpt: drafts.ifa.excerpt,
          content: drafts.ifa.content,
          blog_types: ["insights"],
          status: "draft",
          category: "Adviser Note",
        },
      ];
      const { error } = await supabase.from("blog_posts").insert(rows);
      if (error) throw error;
      toast.success("Both notes saved as drafts in Blog Management");
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>R-range rebalance notes ({rebalanceDate})</DialogTitle>
        </DialogHeader>

        {loading && !drafts && (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Generating drafts from today's R trades…
          </div>
        )}

        {drafts && (
          <Tabs defaultValue="retail" className="w-full">
            <TabsList>
              <TabsTrigger value="retail">Retail client note</TabsTrigger>
              <TabsTrigger value="ifa">IFA / adviser note</TabsTrigger>
            </TabsList>
            {(["retail", "ifa"] as const).map((k) => (
              <TabsContent key={k} value={k} className="space-y-3">
                <div>
                  <Label className="text-xs">Title</Label>
                  <Input value={drafts[k].title} onChange={(e) => patch(k, "title", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Excerpt</Label>
                  <Textarea rows={2} value={drafts[k].excerpt} onChange={(e) => patch(k, "excerpt", e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Content (markdown)</Label>
                  <Textarea rows={18} className="font-mono text-xs" value={drafts[k].content} onChange={(e) => patch(k, "content", e.target.value)} />
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {drafts && (
          <div className="space-y-2 border-t pt-3">
            <Label className="text-xs">Feedback for AI (optional — leave blank to skip)</Label>
            <Textarea
              rows={3}
              placeholder="e.g. tone should be warmer, expand on why we sold X, mention the ESG angle more…"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={loading || !feedback.trim()}
              onClick={() => generate(drafts, feedback.trim())}
            >
              {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Regenerate with feedback
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Close</Button>
          <Button onClick={saveDrafts} disabled={!drafts || saving || loading}>
            {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
            Save both as drafts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
